import Spinner from '@/components/customUI/Loaders/Spinner';
import { useGetTestFromIdQuery } from '@/store/sat/satApi';
import useGetTableColumns, { ActionType } from '@/hooks/useGetTableColumns';
import DataTable from '@/components/customUI/DataTable';
import { formatDate } from '@/lib/utils/dateTime';
import ViewStats from './ViewStats';
import DownloadReport from './DownloadReport';
import Button from '@/components/ui/button';
import RefreshButton from '@/components/svg/RefreshButton';
import ViewRemarks from './ViewRemarks';
import ResultStatus from './ResultStatus';
import { TestByIdMeters } from '@/store/sat/types/records/tests';
import TestCommandResponseModal from './TestCommandResponseModal';
import SubHeader from './SubHeader';
import { useParams } from 'react-router-dom';
import ArgsHeader from '@/pages/hes/command/execution-history/includes/ArgsHeader';
import ErrorScreen from '@/components/customUI/ErrorScreen';
import DownloadResultModal from './download-test-result/DownloadResultModal';
import { useState } from 'react';

type ToggleResult = {
  'NOT AVAILABLE': string;
  SUCCESS: string;
  FAILED: string;
  PROCESSING: string;
};

const ViewTestResult = () => {
  const [open, setOpen] = useState(false);
  const params = useParams();
  const testId = params.test;
  const {
    data: response,
    isFetching,
    isError,
    error,
    refetch
  } = useGetTestFromIdQuery(String(testId), {
    skip: !testId
  });

  const subHeaderObj = {
    'Test Cycle ID': response?.testCycleId || '',
    'Test Id': response?.id || '',
    'Passing Criteria': `${response?.passingCriteria}%`,
    'Exp Res Time': response?.expResTime || ''
  };

  const toggleResult: ToggleResult = {
    'NOT AVAILABLE': '#FFC32E',
    SUCCESS: '#00D1AF',
    FAILED: '#FF716D',
    PROCESSING: '#ABC7FF'
  };

  const Actions: ActionType<TestByIdMeters>[] = [
    {
      element: (props: { data: TestByIdMeters }) => {
        const status = props.data.executionStatus;
        return <ResultStatus status={status} />;
      },
      colName: 'EXECUTION STATUS'
    },
    {
      element: (props) => (
        <TestCommandResponseModal
          {...props}
          commandName={response?.commandInfo.command || ''}
        />
      ),
      colName: 'DETAILS'
    }
  ];

  const columns = useGetTableColumns({
    cols: response && response.meters,
    query: [
      'hesResponseTime',
      'responseData',
      'createdAt',
      'updatedAt',
      'testId',
      'execInfoId',
      'clientRequestId',
      'executionStatus',
      'command'
    ],
    colsToFormat: {
      cols: ['executionStartTime', 'executionEndTime'],
      formatterFunc(val) {
        return formatDate(val, 'DD-MM-YYYY HH:mm:ss');
      }
    },
    action: Actions
  });
  return (
    <div>
      <div className="flex justify-between items-center mt-5">
        <div className="flex flex-col ">
          <div className="text-[#0A3690] font-bold text-2xl">Meters</div>
          {response && <SubHeader data={subHeaderObj} />}
        </div>
        {response && (
          <div>
            <span className="text-[#0A3690] font-bold">Test Result - </span>{' '}
            <span
              className="font-bold"
              style={{
                color:
                  toggleResult[response?.result.result as keyof ToggleResult]
              }}>
              {response?.result.result}
            </span>
          </div>
        )}
      </div>
      {isFetching && (
        <div className="flex h-[80%] justify-center items-center">
          <Spinner />
        </div>
      )}
      {isError && <ErrorScreen error={error} />}
      {!isFetching && response && (
        <div className="overflow-x-scroll flex flex-col">
          <div className="flex flex-wrap mb-2 justify-between items-center gap-2">
            <div>
              <div>
                <span className="font-bold text-[#7C818C]">Command Name:</span>
                <span className="text-sm">{` ${response.commandInfo.command}`}</span>
              </div>
              <ArgsHeader commandArgs={response.commandInfo.args.value} />
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <DownloadReport testId={response.id} />
              <DownloadResultModal
                testStatus={response.status}
                isRespCsvGenerated={response.isRespCsvGenerated}
                open={open}
                setOpen={setOpen}
                testId={testId}
              />
              <Button
                variant="ghost"
                id="refresh-btn-sat-test-result"
                className="refresh-button p-0 min-w-[35px] select-none"
                onClick={refetch}>
                <RefreshButton />
              </Button>
            </div>
          </div>
          <ViewStats data={response.result} />

          {response.remarks && response.remarks !== '' && (
            <ViewRemarks remarks={response.remarks} />
          )}
          <DataTable
            data={(response && response.meters) || []}
            hasInternalPagination={true}
            showFilter
            filterBy={['deviceIdentifier', 'deviceSerial']}
            columnPinning={{
              left: ['id', 'deviceIdentifier', 'deviceSerial'],
              right: []
            }}
            columns={columns}
          />
        </div>
      )}
    </div>
  );
};

export default ViewTestResult;
